/**
 * Company name vs Billing name (owner 2026-10-25).
 *
 * `name` is the Company name: the display name used everywhere in SEED EMERGE.
 * `billingName` is the Billing name: the Xero Contact Name, always.
 *
 * The demarcation (owner 2026-10-25): things synced with Xero (quotes,
 * invoices, credit notes) name their customer by the Billing name
 * (`documentCustomerName`); everything else shows the Company name. No second
 * lines. *SUPERSEDED: "finance screens show the Billing name as a muted second
 * line"* — `secondLineBillingName` / `billingNameDiffers` remain only as
 * comparison helpers, never for display. One copy of the rule lives here.
 */

export const COMPANY_NAME_LABEL = 'Company name';
export const BILLING_NAME_LABEL = 'Billing name';

/**
 * Lowercased, every run of non-[a-z0-9] collapsed to a single space, trimmed.
 *
 * Mirrors backend `src/lib/name-match.ts` `normalizeName` exactly (the server
 * enforces Billing name uniqueness with it). Change both or neither.
 */
export function normalizeCompanyName(s: string): string {
  return s
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();
}

interface CompanyNames {
  name: string;
  billingName?: string | null;
}

/**
 * True when the company carries a non-blank Billing name that normalises to
 * something other than its Company name. `null`/`undefined` (older API or not
 * reported) and blank never differ.
 */
export function billingNameDiffers(company: CompanyNames): boolean {
  const billing = company.billingName;
  if (billing === null || billing === undefined || billing.trim() === '') return false;
  return normalizeCompanyName(billing) !== normalizeCompanyName(company.name);
}

/**
 * 🔴 THE DEMARCATION (owner, 2026-10-25): "in all places of SEED EMERGE it
 * should show company name, on things which get synced up with Xero it should
 * show billing name". Quotes, invoices and credit notes are synced with Xero,
 * so wherever one of those documents names its customer (lists, boards, detail
 * pages, PDF previews, signature emails) use THIS. Everything else uses
 * `company.name`. The Company name is the fallback only for an older API or a
 * blank value, never as a second line.
 */
export function documentCustomerName(company: CompanyNames): string {
  const billing = company.billingName?.trim();
  return billing ? billing : company.name;
}

/** The trimmed Billing name when it differs from the Company name, else `null`. */
export function secondLineBillingName(company: CompanyNames): string | null {
  if (!billingNameDiffers(company)) return null;
  return (company.billingName ?? '').trim();
}
